import { useEffect, useState } from 'react';
import { Modal, Form, Input } from 'antd';
import LockOutlined from '@ant-design/icons/LockOutlined';
import { Box, Typography } from '@mui/material';

interface ConfirmPasswordModalProps {
  open: boolean;
  title?: string;
  onCancel: () => void;
  // Reçoit le mot de passe en clair — ce composant ne connaît pas l'endpoint
  // de vérification ni son contrat exact. L'appelant est responsable de le
  // hacher/chiffrer selon la même convention que UpdatePassword.tsx (Hash()
  // puis Crypter.encrypt()) et d'appeler l'API correspondante. Retourne true
  // si le mot de passe est correct (la modale se ferme), false sinon (reste
  // ouverte, erreur affichée).
  onConfirm: (motDePasse: string) => Promise<boolean> | boolean;
}

/*
 * ============================================================================
 * ConfirmPasswordModal — reconfirmation du mot de passe avant action sensible
 * ----------------------------------------------------------------------------
 * USAGE TYPE (voir ValidationActif.tsx / Kyc/ValidationPanel.tsx) :
 *   1. Clic sur le bouton d'action (Valider/Rejeter) -> setPendingDecision +
 *      ouverture de cette modale (au lieu d'executer l'action directement)
 *   2. L'utilisateur saisit son mot de passe -> onConfirm(motDePasse) :
 *        const payload = { coduti: user.coduti, motpas: Hash(motDePasse) };
 *        POST Data.ApiUrl.verifyPassword, header CipherUser = payload chiffre
 *        (meme convention Hash() + Crypter.encrypt() que UpdatePassword.tsx)
 *   3. Retourne true -> la modale se ferme, l'appelant execute l'action ;
 *      false -> la modale reste ouverte, "Mot de passe incorrect." affiche
 *
 * FLOW REJET (ValidationActif) : le mot de passe est verifie AVANT
 * d'afficher le champ motif ; le 2e clic sur Rejeter soumet le rejet.
 *
 * DESIGN UI : Modal antd 520px, titre + icone LockOutlined, un seul champ
 * Input.Password (size large, autoFocus), boutons Confirmer/Annuler,
 * confirmLoading pendant la verification. Pur UI, reutilisable partout —
 * ne fait aucun appel reseau lui-meme (voir le contrat onConfirm ci-dessus).
 * ============================================================================
 */
export default function ConfirmPasswordModal({ open, title, onCancel, onConfirm }: ConfirmPasswordModalProps) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [erreur, setErreur] = useState('');

  useEffect(() => {
    if (open) {
      form.resetFields();
      setErreur('');
    }
  }, [open, form]);

  async function handleOk() {
    try {
      const values = await form.validateFields();
      setLoading(true);
      setErreur('');
      const ok = await onConfirm(values.motDePasse);
      if (!ok) setErreur('Mot de passe incorrect.');
    } catch {
      // Erreur de validation du formulaire (champ vide) — rien à faire, la
      // modale reste ouverte, antd affiche déjà l'erreur sous le champ.
    } finally {
      setLoading(false);
    }
  }

  return (
    <Modal
      open={open}
      title={
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LockOutlined />
          <span>{title ?? 'Confirmation requise'}</span>
        </Box>
      }
      onCancel={onCancel}
      onOk={handleOk}
      okText="Confirmer"
      confirmLoading={loading}
      okButtonProps={{ style: { color: '#fff' } }}
      cancelText="Annuler"
      destroyOnClose
      width={520}
    >
      <Form form={form} layout="vertical" onFinish={handleOk}>
        <Form.Item
          name="motDePasse"
          label="Mot de passe"
          rules={[{ required: true, message: 'Mot de passe requis.' }]}
        >
          <Input.Password autoFocus placeholder="Votre mot de passe" size="large" />
        </Form.Item>
      </Form>
      {erreur && (
        <Typography variant="body2" color="error" sx={{ mt: -1 }}>
          {erreur}
        </Typography>
      )}
    </Modal>
  );
}
